/**
 * GL2026 Logger
 * Levelled client-side logging built on loglevel.
 * - In non-production: DEBUG and above are emitted to the console.
 * - In production:     Only WARN and ERROR reach the console.
 * - WARN / ERROR entries are persisted in a localStorage rolling buffer
 *   (last MAX_BUFFER entries) so they survive page reloads.
 * - downloadLogs() exports the buffer as a timestamped .txt file.
 * - Global window.onerror and unhandledrejection are hooked automatically.
 */

import log from 'loglevel';

const PREFIX     = '[GL2026]';
const BUFFER_KEY = 'gl2026_log_buffer';
const MAX_BUFFER = 500;
const IS_PROD    = process.env.NODE_ENV === 'production';

// ── Level setup ──────────────────────────────────────────────────────────────
log.setDefaultLevel(IS_PROD ? log.levels.WARN : log.levels.DEBUG);

// ── localStorage buffer helpers ───────────────────────────────────────────────
function readBuffer() {
  try {
    return JSON.parse(localStorage.getItem(BUFFER_KEY) || '[]');
  } catch (_) {
    return [];
  }
}

function writeBuffer(entries) {
  try {
    localStorage.setItem(BUFFER_KEY, JSON.stringify(entries));
  } catch (_) { /* storage full – silently ignore */ }
}

function appendBuffer(level, args) {
  const entries = readBuffer();
  entries.push({
    ts:  new Date().toISOString(),
    lvl: level,
    msg: args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ')
  });
  if (entries.length > MAX_BUFFER) entries.splice(0, entries.length - MAX_BUFFER);
  writeBuffer(entries);
}

// ── Public API ────────────────────────────────────────────────────────────────
const Logger = {
  debug(...args) {
    log.debug(PREFIX, ...args);
  },

  info(...args) {
    log.info(PREFIX, ...args);
  },

  warn(...args) {
    log.warn(PREFIX, ...args);
    appendBuffer('WARN', args);
  },

  error(...args) {
    log.error(PREFIX, ...args);
    appendBuffer('ERROR', args);
  },

  /** Return raw buffer entries (array of { ts, lvl, msg }). */
  getLogs() {
    return readBuffer();
  },

  /** Clear the persisted buffer. */
  clearLogs() {
    writeBuffer([]);
  },

  /**
   * Trigger a browser download of the current log buffer as a plain-text file.
   * Safe to call from error handlers – does not throw.
   */
  downloadLogs() {
    try {
      const entries = readBuffer();
      if (entries.length === 0) {
        log.info(PREFIX, 'No buffered logs to download.');
        return;
      }
      const lines = entries.map(e => `${e.ts} [${e.lvl}] ${e.msg}`).join('\n');
      const date  = new Date().toISOString().slice(0, 10);
      const blob  = new Blob([lines], { type: 'text/plain;charset=utf-8' });
      const url   = URL.createObjectURL(blob);
      const link  = document.createElement('a');
      link.href     = url;
      link.download = `gl2026-debug-${date}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      log.error(PREFIX, 'downloadLogs failed:', e);
    }
  }
};

// ── Global error listeners (attached once at module load) ─────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    Logger.error('Uncaught error:', event.message, '|', event.filename, ':', event.lineno);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    Logger.error(
      'Unhandled promise rejection:',
      reason instanceof Error ? reason.stack || reason.message : String(reason)
    );
  });
}

export default Logger;
